import React, { useEffect, useRef } from 'react'
import { QueryClientProvider } from '@tanstack/react-query'
import { isItemToggled, TreeData, TreeNode, useTreeActions, useTreeData, useTreeStore, useTreeStoreInstance } from '../model'
import { TreeNodeToggle } from './tree-node-toggle'
import { mergeClasses } from '@/helpers/styling'
import { requireNonNull } from "@/helpers/require-non-null"
import { useTreeConfig } from '../config'
import {
  TreePerRowJob, treeNodeExpressionVars, splitNativeActions,
  useDispatchTreeActionsMutation, useDispatchTreeActionMutation,
} from "../mutation/tree-action-dispatch"
import { isMacOS } from '@/utils/operating-system'
import { queryClient } from '@/queryClient'
import { ActionButtonRenderer } from '@/shared/action-buttons/action-button-renderer'
import { TreeNodeChrome, TreeNodeChromeHandle } from './tree-node-chrome'
import {
  DndDropIndicator,
  DndGroupDropIndicator,
  DndOperation,
  useTreeNodeDnd,
  useTreeRootDnd,
} from '../../../dnd/tree-dnd'
import { toast } from "sonner"
import { TreePerRowAction, TreePerRowButtonsConfig } from '../config-schema'
import { ProvideVisibility, useNearestVisibility } from "../../../../hooks/use-nearest-visibility"

export const TreeNodesViewRoot = React.memo(function TreeNodesViewRoot(props: {
  treeData: TreeData
  loading?: boolean
  setLoading?: (isLoading: boolean) => void
}) {
  const ref = useRef<HTMLDivElement>(null)
  const treeConfig = useTreeConfig()
  const treeId = useTreeStore(st => st.id)

  const mutation = useDispatchTreeActionMutation()

  const [ dndState, updateDnd ] = useTreeRootDnd({
    enabled: Boolean(treeConfig.dndEnabled),
    treeId,
    onDrop: (instruction, source, target) => {
      if (treeConfig.native?.onDrop) {
        treeConfig.native.onDrop(instruction, source, target)
        return
      }

      if (instruction.blocked) {
        toast.warning("Cannot move node here")
        return
      }


      const sourceNode = requireNonNull(props.treeData.nodesById[source])
      const targetNode =  requireNonNull(props.treeData.nodesById[target])
      if (instruction.operation === "combine") {
        // TODO: errors get swallowed
         mutation.mutate({
          type: "reparent-node",
          onSuccess: treeConfig.onReparentSuccessConfig,
          input: { node: sourceNode, parent: targetNode },
        })
      } else {
        // TODO: errors get swallowed
        mutation.mutate({
          type: "move-node",
          onReparented: treeConfig.onReparentSuccessConfig,
          onReordered: treeConfig.onReorderSuccessConfig,
          input: {
            node: sourceNode,
            target: targetNode,
            reorder: instruction.operation === "reorder-before" ? "before" : "after"
          }
        })
      }
    },
    rootRef: ref
  })
  useSubscribeToExpansionChange(updateDnd)

  // May want to remove this when optimistic update implemented
  // useEffect(() => {
  //   props.setLoading(mutation.isPending)
  //   // eslint-disable-next-line react-hooks/exhaustive-deps
  // }, [ mutation.isPending ])

  return (
    <div ref={ref} className="position-relative">
      <DndGroupDropIndicator isActive={dndState === "is-innermost-over"} />
      <ProvideVisibility element={ref}>
        <TreeNodesView nodes={props.treeData.rootNodeIds.map(n => props.treeData.nodesById[n])} />
      </ProvideVisibility>
    </div>
  )
})

function useSubscribeToExpansionChange(action: () => void) {
  const treeStore = useTreeStoreInstance()
  useEffect(() => {
    return treeStore.subscribe(
      s => s.client.expandedNodes,
      action)
    }, []
  )
}

export const TreeNodesView = React.memo(function TreeNodesView(props: {
  nodes: TreeNode[]
  depth?: number
}) {
  return (
    <ul role={props.depth == null ? "tree" : "group"}>
      {props.nodes.map(node => (
        <li key={node.id} className="position-relative">
          <TreeNodeView depth={props.depth ?? 0} node={node} />
        </li>
      ))}
    </ul>
  )
})

const TreeNodeView = React.memo(function TreeNodeView(props: {
  node: TreeNode
  depth: number
}) {
  const groupRef = useRef<HTMLDivElement>(null)
  const treeData = useTreeData()
  const treeNodeHandle = useRef<TreeNodeChromeHandle>(null)

  const checkState = useTreeStore(st => st.computed.checkStateByNode[props.node.id])
  const isExpanded = useTreeStore(st => Boolean(st.client.expandedNodes[props.node.id]))
  const isSelected = useTreeStore(st => st.client.selectedItems.has(props.node.itemId))
  const allEffects = useTreeStore(st => st.client.effectQueue)
  const effects = React.useMemo(() => allEffects.filter(e => e.nodeId === props.node.id), [ allEffects, props.node.id ])
  const treeActions = useTreeActions()
  const treeConfig = useTreeConfig()
  const treeId = useTreeStore(st => st.id)

  const item = treeData.itemsById[props.node.itemId]
  const hasChildren = props.node.childrenIds.length > 0

  const variant = treeConfig.variant ?? "default"
  const dndEnabled = Boolean(treeConfig.dndEnabled)
  const dndDragActivationMode = treeConfig.dndDragActivationMode ?? "handle"

  const toggles = treeConfig.togglesConfig ?? []
  const toggledItems = useTreeStore(st => st.client.toggledItems)
  const hasButtons = Boolean(treeConfig.buttonsConfig?.perRow.length) || toggles.length > 0

  const dispatchAction = useDispatchTreeActionsMutation()
  const expressionVars = React.useMemo(
    () => treeNodeExpressionVars(props.node, treeData),
    [ props.node, treeData ])

  const visible = useNearestVisibility()
  React.useEffect(() => {
    const target = treeNodeHandle.current?.row
    if (!target) return
    if (!visible) return

    for (const effect of effects) {
      try {
        switch (effect.type) {
          case 'reveal':
            target.scrollIntoView({ behavior: effect.scrollBehaviour })
            break

          default:
            throw new Error(`Unrecognized Tree++ effect type ${effect.type}`)

        }
      } finally {
        effect.settled()
      }
    }
  }, [ effects, visible ])

  // QueryClientProvider necessary for DynamicIcons to work
  const renderPreview = () => {
    return (
      <QueryClientProvider client={queryClient}>
        <div className={mergeClasses("min-w-80")}>
          <TreeNodeChrome variant={variant}
                          node={props.node}
                          item={item}
                          depth={0}
                          checkboxLayout={treeConfig.checkboxLayout ?? "nested"}
                          withCheckbox={Boolean(treeConfig.checkboxesEnabled)}
                          withChevron={hasChildren}
                          withDragHandle={dndEnabled && dndDragActivationMode === "handle"}
                          checkState={checkState} />
        </div>
      </QueryClientProvider>
    )
  }

  const renderDropInstruction = () => {
    if (!treeConfig.dndEnabled) return null
    if (!dndState.instruction) return null
    return <DndDropIndicator instruction={dndState.instruction} />
  }

  const [ dndState, updateDnd ] = useTreeNodeDnd({
    enabled: Boolean(treeConfig.dndEnabled),
    treeId,
    nodeId: props.node.id,
    reparentFeatureEnabled: Boolean(treeConfig.dndReparentEnabled),
    reorderFeatureEnabled: Boolean(treeConfig.dndReorderEnabled),
    isValidDropTarget: (op, src, tgt) => isValidDropTarget(treeData, op, src, tgt),
    isNodeExpanded: isExpanded,
    isNodeExpandable: !isExpanded && props.node.childrenIds.length > 0,
    setNodeExpanded: expanded => treeActions.toggleNodeExpanded(props.node.id, expanded),
    getRow: () => treeNodeHandle.current?.row,
    getGroup: () => groupRef.current,
    getDragHandle: () =>
      dndDragActivationMode === "handle"
        ? treeNodeHandle.current?.dragHandle
        : treeNodeHandle.current?.row,
    renderPreview: renderPreview
  })
  useSubscribeToExpansionChange(updateDnd)
  useEffect(updateDnd, [ dndDragActivationMode ])

  return (
    <>
      <TreeNodeChrome
        ref={treeNodeHandle}
        node={props.node}
        item={item}
        variant={variant}
        depth={props.depth}
        selected={isSelected}
        checkboxLayout={treeConfig.checkboxLayout ?? "nested"}
        withCheckbox={Boolean(treeConfig.checkboxesEnabled)}
        withChevron={hasChildren}
        withDragHandle={dndEnabled && dndDragActivationMode === "handle"}
        withButtons={Boolean(hasButtons)}
        checkState={checkState}
        onSetChecked={checked => treeActions.toggleNodeChecked(props.node.id, checked)}
        expanded={isExpanded}
        onSetExpanded={expanded => treeActions.toggleNodeExpanded(props.node.id, expanded)}
        onMouseEnter={() => treeConfig.native?.onMouseEnter?.(props.node)}
        onMouseLeave={() => treeConfig.native?.onMouseLeave?.(props.node)}
        classNames={{
          wrapper: mergeClasses({
            "opacity-20": dndState.node === "dragging"
          }),
          row: mergeClasses({
            'cursor-pointer': treeConfig.selectMode !== 'disabled',
            "!bg-red-200": !!dndState.instruction?.blocked
          })
        }}
        renderDropInstruction={renderDropInstruction}
        renderButtons={() => (
          <>
          {toggles.map(toggle => (
            <TreeNodeToggle
              key={toggle.id}
              toggle={toggle}
              toggled={isItemToggled(treeData, toggledItems, toggle.id, props.node.itemId)}
              variant={variant}
              onToggle={toggled => treeActions.toggleNode(toggle.id, props.node.id, toggled)}
            />
          ))}
          <ActionButtonRenderer
            variant={variant}
            buttons={(treeConfig.buttonsConfig?.perRow ?? []) as TreePerRowButtonsConfig}
            dispatch={(actions) => {
              const input = { node: props.node }
              const { native, regular } = splitNativeActions(actions)
              if (native.length > 0) {
                treeConfig.native?.dispatch?.perRow?.(props.node, native.map(action => ({ ...action, input })))
              }
              if (regular.length > 0) {
                dispatchAction.perRow.mutate(regular.map((action): TreePerRowJob => ({ ...action, input })))
              }
            }}
            expressionVars={expressionVars}
            popoverContentClassName={treeConfig.native?.popoverContentClasses}
            popoverContentStyle={{ fontFamily: treeConfig.fontFamily }}
          />
          </>
        )}
        onClick={e => {
          if (treeConfig.selectMode === 'disabled') return
          if (treeConfig.disableDeselection && isSelected) return
          const mode = (() => {
            if (treeConfig.selectMode !== 'multiple') return 'single'
            if (e.shiftKey) return 'range'
            if ((isMacOS && e.metaKey) || e.ctrlKey) return 'toggle'
            return 'single'
          })()
          treeActions.select(props.node.id, mode)
        }}
      />
      {hasChildren && isExpanded && (
        <div ref={groupRef} className="position-relative">
          <DndGroupDropIndicator
            isActive={dndState.group === "is-innermost-over"}
            leftPadding={((props.depth + 1) * 16)} />
          <TreeNodesView
            depth={props.depth + 1}
            nodes={props.node.childrenIds.map(id => treeData.nodesById[id])} />
        </div>
      )}
    </>
  )
})

function isValidDropTarget(treeData: TreeData, op: DndOperation, srcId: string, tgtId: string) {
  const tgtNode = treeData.nodesById[tgtId]
  const tgtItem = treeData.itemsById[tgtNode.itemId]
  if (op === "combine") {
    return tgtItem.canDropInto ?? true
  } else {
    return true
  }
}
